import { NgFor, NgIf } from "@angular/common";
import {
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  signal,
  untracked,
} from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { MatCheckboxChange, MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatTabsModule } from "@angular/material/tabs";
import { Router } from "@angular/router";
import { decimalNumber, iso2Pattern, phoneNumberPattern } from "@app/regex";
import { AiTagSelectComponent } from "@components/ai-tag-select/ai-tag-select.component";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import {
  IMediaPicker,
  MediaPickerComponent,
} from "@components/media-picker/media-picker.component";
import { ProductOptionsComponent } from "@components/product-options/product-options.component";
import { TagAutocompleteComponent } from "@components/tag-autocomplete/tag-autocomplete.component";
import { ngxEditorConfig } from "@config/angular-editor.config";
import { AngularEditorModule } from "@kolkov/angular-editor";
import { IApiResponse } from "@models/api-response.model";
import { NOT_APPLICABLE } from "@models/packing-sizes";
import {
  ICategoryList,
  IProductOptionRequestDto,
  IProductRequestDto,
  IProductResponseDto,
} from "@models/product-list.model";
import { AppStateService, RequestService } from "@services/index";
import { ProductService } from "@services/product/product.service";
import {
  catchError,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  firstValueFrom,
  map,
  of,
  Subscription,
  startWith,
  switchMap,
} from "rxjs";
import { IVendorList } from "../../models";
import { Country } from "../../models/country.model";
import { printFormValueAndValidity } from "../../utils";
import { ChipsMultiSelectComponent } from "../chips-multi-select/chips-multi-select.component";
import {
  ALL_ZONE_CONTROL_NAMES,
  deliveryZonesFor,
  highestZoneFee,
  type ZoneFees,
  zoneControlName,
} from "../us-delivery-zones";
import { ProductControls, ProductListingStatus } from "./product-controls.enum";
import {
  getControlsForGeneralCategory,
  getSegmentForCategory,
  isLivestockCategoryName,
  LIVESTOCK_SEGMENT,
  TANK_SEGMENT,
} from "./product-input-controls";

/** An option's markup already is `markup` (empty and null alike: the backend fills it in). */
const sameMarkup = (value: unknown, markup: number | null) =>
  markup === null
    ? value === null || value === undefined || value === ""
    : value !== null && value !== "" && Number(value) === markup;

export interface ICountrySpecificInfo {
  iso2: string;
  displayPrice: number;
  deliveryCharge: number;
  destinationTranshipperMarkup: number;
  productOptions: IProductOptionRequestDto[];
}

interface ZodValidationError {
  path: string;
  message: string;
  code: string;
}

export const AI_TAG_VOCABULARY: string[] = [
  "Beginner Friendly",
  "Intermediate",
  "Expert Only",
  "Schooling",
  "Peaceful",
  "Semi-Aggressive",
  "Aggressive",
  "Nocturnal",
  "Bottom Dweller",
  "Surface Dweller",
  "Cave Dweller",
  "Nano Tank",
  "Community Tank",
  "Species Only Tank",
  "Planted Tank",
  "Blackwater",
  "Biotope",
  "Needs Lid",
  "Herbivore",
  "Carnivore",
  "Omnivore",
  "Algae Eater",
  "Heavy Feeder",
  "Shrimp Safe",
  "Snail Safe",
  "Invertebrate Friendly",
  "Not for Small Fish",
  "Hardy",
  "Sensitive",
  "Live Bearer",
  "Oddball",
  "Fast Breeder",
  "Air Breather",
  "Colour Changing",
  "Tropical",
  "Coldwater",
  "Warm Water",
  "Hard Water",
  "Soft Water",
  "Brackish",
  // Rarity (replaces "Rare & Exotic")
  "Rare",
  "Super Rare",
  // Colour
  "Albino",
  "Black",
  "Blue",
  "Brown",
  "Copper",
  "Electric Blue",
  "Galaxy",
  "Gold",
  "Green",
  "Koi",
  "Multicolour",
  "Neon",
  "Orange",
  "Pink",
  "Platinum / Silver",
  "Purple",
  "Rainbow",
  "Red",
  "White",
  "Yellow",
  // Sex
  "Male",
  "Female",
  // Plants — care, placement, traits
  "Easy Care",
  "Low Light",
  "High Light",
  "CO2 Required",
  "No CO2 Required",
  "Carpet",
  "Foreground",
  "Midground",
  "Background",
  "Epiphyte",
  "Floating",
  "Moss",
  "Emersed / Wabi-Kusa",
  "Fast-Growing",
  "Flowering",
];

@Component({
  selector: "fh-product-input",
  templateUrl: "./product-input.component.html",
  styleUrls: ["./product-input.component.scss"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatFormFieldModule,
    FormAutocompleteComponent,
    ChipsMultiSelectComponent,
    TagAutocompleteComponent,
    AiTagSelectComponent,

    ProductOptionsComponent,
    AngularEditorModule,
    MediaPickerComponent,
    MatChipsModule,
    MatIconModule,
    MatCheckboxModule,
    MatTabsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
    NgIf,
    NgFor,
  ],
})
export class ProductInputComponent implements OnDestroy {
  productId = input.required<number>();

  initialSelectedCategory = input.required<number>();
  isCategoryEditable = input.required<boolean>();
  formChanged = output<IProductRequestDto>();
  formIsValid = output<boolean>();
  productCategoryId = output<number>();
  /**
   * The product's score, 0-100, as the API computed it. Emitted rather than
   * fetched again by the page: it arrives with the product, and a second request
   * for one number would be a second chance to disagree with the first.
   */
  productScore = output<number | null>();

  categoryEditable$ = toObservable(this.isCategoryEditable);
  initialSelectedCategory$ = toObservable(this.initialSelectedCategory);
  // The description is rich text, like SEO pages and blogs: bold, italic,
  // underline, headings, lists, links, alignment and the <> source view.
  // Fonts, colours and sizes stay hidden so descriptions keep the site's own
  // style, and images and videos stay with the product's media, not inline.
  editorConfig = {
    ...ngxEditorConfig,
    enableToolbar: true,
    showToolbar: true,
    toolbarHiddenButtons: [
      ["subscript", "superscript", "indent", "outdent", "fontName"],
      ["fontSize", "textColor", "backgroundColor", "customClasses", "insertImage", "insertVideo"],
    ],
  };
  /** US prices delivery by the customer's state across these five zones. */
  readonly deliveryZonesFor = deliveryZonesFor;
  readonly zoneControlName = zoneControlName;
  readonly aiTagVocabulary = AI_TAG_VOCABULARY;
  productForm!: FormGroup;
  productExpiryHintText!: string;

  productListingStatusOptions = [
    {
      key: ProductListingStatus.Active,
      value: "Active",
    },
    {
      key: ProductListingStatus.Inactive,
      value: "Inactive",
    },
    {
      key: ProductListingStatus.AuctionActive,
      value: "Auction Active",
    },
  ];

  countrySpecificForm!: FormGroup;
  selectedCountries = signal<string[]>([]);
  // Track countries the user has manually collapsed — everything else is expanded by default
  collapsedCountries = signal<Set<string>>(new Set());

  // Add property to store vendor defaults
  private vendorDefaults: Record<
    string,
    {
      destination_transhipper_markup: number;
      standard_delivery_fee: number;
      delivery_zone_fees?: ZoneFees | null;
      /** The market's transhipper markup per bag / box size (the Transhipment page). */
      size_markups?: Record<string, number>;
    }
  > = {};
  /**
   * Option markups as they were when Request Quote was turned on, put back when
   * it is turned off. Plain fields, not signals: written from form events.
   */
  private markupsBeforeRequestQuote = new WeakMap<AbstractControl, number>();
  /** Options loaded on a product already on Request Quote: saved with markup 0. */
  private optionsLoadedOnRequestQuote = new WeakSet<AbstractControl>();

  originCountries = signal<Country[]>([]);
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  vendors = signal<IVendorList>([]);
  categories = computed(() => this.appStateService.parentCategories());
  subCategories = signal<ICategoryList>([]);
  subSubCategories = signal<ICategoryList>([]);

  // Autocomplete options computed properties
  originCountryOptions = computed<FormAutocompleteOption<string>[]>(() =>
    this.originCountries().map((c) => ({ value: c.iso2, label: c.name }))
  );

  /**
   * Vendor Country: picking one narrows the vendor list to that country's
   * vendors. It opens on the vendor's own country, so the two always agree.
   */
  readonly vendorCountry = signal<string>("");

  vendorOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.vendors()
      // Vendors without an iso2 always stay, so the list is never empty when
      // the backend leaves it out.
      .filter(
        (v) =>
          !this.vendorCountry() ||
          !v.iso2 ||
          v.iso2.toUpperCase() === this.vendorCountry().toUpperCase()
      )
      .map((v) => ({ value: +v.id, label: v.name }))
  );

  categoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.displayCategories().map((c) => ({ value: c.id, label: c.name }))
  );

  subCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.subCategories().map((c) => ({ value: c.id, label: c.name }))
  );

  subSubCategoryOptions = computed<FormAutocompleteOption<number>[]>(() =>
    this.subSubCategories().map((c) => ({ value: c.id, label: c.name }))
  );

  temperamentOptions: FormAutocompleteOption<string>[] = [
    { value: "Aggressive", label: "Aggressive" },
    { value: "Semi Aggressive", label: "Semi Aggressive" },
    { value: "Peaceful", label: "Peaceful" },
  ];

  waterConditionOptions: FormAutocompleteOption<string>[] = [
    { value: "High PH", label: "High PH" },
    { value: "Low PH", label: "Low PH" },
  ];

  wysiwygOptions: FormAutocompleteOption<string>[] = [
    { value: "exact", label: "Exact Fish Shown" },
    { value: "representative", label: "Representative Photo Only" },
  ];

  booleanOptions: FormAutocompleteOption<boolean>[] = [
    { value: false, label: "No" },
    { value: true, label: "Yes" },
  ];

  statusOptions = computed<FormAutocompleteOption<string>[]>(() =>
    this.productListingStatusOptions.map((s) => ({ value: s.key, label: s.value }))
  );

  /**
   * What the main-category dropdown offers for the chosen form.
   *
   * Livestock now covers more than one main category — Freshwater and Marine —
   * so the livestock form offers all of them rather than pinning to id 1. Tank
   * still has exactly one. "Others" excludes anything that has its own form, so
   * a livestock category can never be reached through the generic form and lose
   * its livestock fields.
   */
  displayCategories = computed(() => {
    const all = this.categories();
    const segment = getSegmentForCategory(this.initialSelectedCategory(), all);

    if (segment === LIVESTOCK_SEGMENT) {
      return all.filter((c) => isLivestockCategoryName(c.name));
    }
    if (segment === TANK_SEGMENT) {
      return all.filter((c) => c.id === TANK_SEGMENT);
    }
    return all.filter((c) => !isLivestockCategoryName(c.name) && c.id !== TANK_SEGMENT);
  });

  /**
   * The form (Livestock / Tank / Others) a new product is built with. A computed,
   * so categories arriving late only rebuild the form if they change the segment.
   */
  private readonly newProductSegment = computed(() =>
    getSegmentForCategory(this.initialSelectedCategory(), this.categories())
  );

  minDate = new Date();
  maxDate = new Date(new Date().setDate(new Date().getDate() + 30));
  private formSubscription?: Subscription;
  private restrictionSubscription?: Subscription;
  private categorySubscription?: Subscription;

  /**
   * Markets the cross-border restrictions (admin portal > Cross Border
   * Restriction) block for this product's category from its vendor's country.
   * Their checkboxes are greyed out; the save refuses them too.
   */
  readonly restrictedMarkets = signal<string[]>([]);

  isMarketRestricted(iso2: string): boolean {
    return this.restrictedMarkets().includes(iso2.toUpperCase());
  }

  restrictionText(iso2: string): string {
    return `Not allowed: cross-border restriction for this category into ${iso2.toUpperCase()}`;
  }

  /**
   * The markets the selected vendor is enabled for (admin portal > Vendor >
   * Markets); null until known, which allows every market. As in the merchant
   * portal, a market outside them cannot be added to a product.
   */
  readonly allowedMarkets = signal<string[] | null>(null);
  private allowedMarketsSubscription?: Subscription;

  /**
   * Markets the product was listed in when it was loaded. They stay allowed,
   * as in the merchant portal and on the server (blocking a market does not
   * take products down), so one unticked by accident can be ticked again.
   */
  private readonly loadedMarkets = signal<string[]>([]);

  isMarketAllowed(iso2: string): boolean {
    const allowed = this.allowedMarkets();
    const market = iso2.toUpperCase();
    return !allowed || allowed.includes(market) || this.loadedMarkets().includes(market);
  }

  /** Greyed out: not enabled for this vendor, or restricted for this category. */
  marketBlocked(iso2: string): boolean {
    return !this.isMarketAllowed(iso2) || this.isMarketRestricted(iso2);
  }

  marketBlockText(iso2: string): string {
    return this.isMarketRestricted(iso2)
      ? this.restrictionText(iso2)
      : "Not enabled for this merchant (Vendor > Markets)";
  }

  /** Re-reads the vendor's enabled markets whenever the vendor changes. */
  private watchAllowedMarkets() {
    this.allowedMarketsSubscription?.unsubscribe();
    this.allowedMarketsSubscription = this.productForm.valueChanges
      .pipe(
        startWith(this.productForm.value),
        map((v: Record<string, unknown>) => Number(v?.[ProductControls.VendorId]) || 0),
        distinctUntilChanged(),
        switchMap((vendorId) => {
          if (!vendorId) return of<string[] | null>(null);
          return this.requestService.get<IApiResponse>(`/api/v2/vendor/${vendorId}/markets`).pipe(
            map((res) =>
              res.status
                ? ((res.data as { allowedMarkets: string[] })?.allowedMarkets ?? []).map((m) =>
                    m.toUpperCase()
                  )
                : null
            ),
            catchError(() => of(null))
          );
        })
      )
      .subscribe((markets) => {
        this.allowedMarkets.set(markets);
        // Re-emit formIsValid: a ticked market the vendor is not enabled for blocks Save.
        this.countrySpecificForm?.updateValueAndValidity();
      });
  }

  /** Re-checks the restrictions whenever the vendor or category changes. */
  private watchRestrictions() {
    this.restrictionSubscription?.unsubscribe();
    const keys = [
      ProductControls.VendorId,
      ProductControls.CategoryId,
      ProductControls.SubcategoryId,
      ProductControls.SubsubcategoryId,
    ];
    this.restrictionSubscription = this.productForm.valueChanges
      .pipe(
        startWith(this.productForm.value),
        map((v: Record<string, unknown>) => keys.map((k) => Number(v?.[k]) || 0)),
        distinctUntilChanged((a, b) => a.join() === b.join()),
        debounceTime(200),
        // switchMap: a newer vendor/category cancels the older request, so a
        // stale response can never overwrite the current one.
        switchMap(([vendorId, categoryId, subcategoryId, subsubcategoryId]) => {
          if (!vendorId || !categoryId) return of<string[]>([]);
          const query = new URLSearchParams({
            vendorId: String(vendorId),
            categoryId: String(categoryId),
            subcategoryId: String(subcategoryId),
            subsubcategoryId: String(subsubcategoryId),
          });
          return this.requestService
            .get<IApiResponse>(`/api/v2/cross-border-restrictions/blocked?${query}`)
            .pipe(
              map((res) =>
                res.status ? ((res.data as { markets: string[] })?.markets ?? []) : null
              ),
              catchError(() => of(null))
            );
        })
      )
      .subscribe((markets) => {
        if (markets === null) return;
        this.restrictedMarkets.set(markets);
        // Re-emit formIsValid: a ticked market that is now restricted blocks Save.
        this.countrySpecificForm?.updateValueAndValidity();
      });
  }

  zodErrors = signal<ZodValidationError[]>([]);
  isGeneratingDescription = signal(false);

  // Loading state for the destination-country list that drives the per-country
  // pricing/options section. The list is normally loaded once at app bootstrap;
  // these let the template show a spinner / retry instead of silently vanishing.
  countriesLoading = signal(false);
  countriesLoadFailed = signal(false);

  constructor(
    private readonly router: Router,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly requestService: RequestService,
    private readonly productService: ProductService,
    private readonly appStateService: AppStateService
  ) {
    // Only the product id, and for a new product the form segment, rebuild the
    // form. Everything else is read untracked: categories arriving late must not
    // wipe what was typed into a new product (or re-fetch its vendors).
    effect(() => {
      if (!this.productId()) this.newProductSegment();
      untracked(() => this.initializePage());
    });
  }

  initializePage() {
    // untracked: this reads/writes the countries signals but must not make the
    // init effect re-run (which would re-fetch the product) when they change.
    untracked(() => this.ensureCountriesLoaded());
    this.fetchOriginCountries();
    if (!this.productId()) {
      this.buildForm({} as IProductResponseDto);
    } else {
      const id = this.productId();
      this.requestService
        .get<IApiResponse<IProductResponseDto>>(`/api/v2/product?productId=${id}`)
        .subscribe({
          next: (res) => {
            // finally: a throw while building must not leave the page waiting.
            try {
              if (res.status === true) {
                this.productCategoryId.emit(res.data.category_id);
                this.productScore.emit(
                  (res.data as unknown as { score?: number | null }).score ?? null
                );
                this.buildForm(res.data);
              } else {
                this.router.navigate(["/products"]);
              }
            } finally {
              this.settleLoad(id);
            }
          },
          error: () => this.settleLoad(id),
        });
    }
  }

  /** The page waiting for the load a product id it just set triggers. */
  private pendingLoad?: { id: number; resolve: () => void };

  /**
   * Resolves once the init effect's load of `id` is done (or failed). For a
   * product just created: setting its id already reloads the form, so the page
   * waits for that load instead of starting a second one.
   */
  whenLoaded(id: number): Promise<void> {
    return new Promise((resolve) => {
      this.pendingLoad = { id, resolve };
    });
  }

  private settleLoad(id: number) {
    if (this.pendingLoad?.id !== id) return;
    const { resolve } = this.pendingLoad;
    this.pendingLoad = undefined;
    resolve();
  }

  /**
   * Rebuilds the form from the product as saved. The page stays open after
   * Save, and options added in this session would otherwise keep state "add"
   * with no id - a second Save would insert them again.
   */
  /**
   * Rebuilds the form from what was stored; resolves once done (or the load
   * failed). `id` for a product just created, before the input has caught up.
   */
  reloadFromServer(id: number = this.productId()): Promise<void> {
    if (!id) return Promise.resolve();
    return new Promise((resolve) => {
      this.requestService
        .get<IApiResponse<IProductResponseDto>>(`/api/v2/product?productId=${id}`)
        .subscribe({
          next: (res) => {
            // finally: a throw while building must not leave Save disabled.
            try {
              if (res.status === true) {
                // The save may have changed what the score counts (images, videos, description).
                this.productScore.emit(
                  (res.data as unknown as { score?: number | null }).score ?? null
                );
                this.buildForm(res.data);
              }
            } finally {
              resolve();
            }
          },
          error: () => resolve(),
        });
    });
  }

  fetchOriginCountries() {
    this.requestService
      .get<IApiResponse<Country[]>>(`/api/v2/origin-country-list`)
      .subscribe((res) => {
        this.originCountries.set(res.data);
      });
  }

  /**
   * The destination-country list is normally loaded once at app bootstrap
   * (main.ts). If that call was slow or failed, the shared list can be empty and
   * the per-country pricing/options section silently disappears. Re-fetch it here
   * when it's missing so a single slow/failed startup request doesn't kill the
   * section for the whole session; the template shows a spinner / retry.
   */
  private ensureCountriesLoaded() {
    if (this.appStateService.allCountries().length > 0) return; // already loaded
    if (this.countriesLoading()) return; // in-flight
    this.countriesLoading.set(true);
    this.countriesLoadFailed.set(false);
    this.requestService.get<IApiResponse<Country[]>>(`/api/v2/country`).subscribe({
      next: (res) => {
        this.countriesLoading.set(false);
        if (res?.status === true && Array.isArray(res.data) && res.data.length > 0) {
          this.appStateService.allCountries.set(res.data);
        } else {
          this.countriesLoadFailed.set(true);
        }
      },
      error: () => {
        this.countriesLoading.set(false);
        this.countriesLoadFailed.set(true);
      },
    });
  }

  retryLoadCountries() {
    this.ensureCountriesLoaded();
  }

  buildForm(data: IProductResponseDto) {
    // A rebuild (after Save) re-adds every saved country below; without this
    // reset each would be listed twice. untracked: may run inside the init effect.
    untracked(() => this.selectedCountries.set([]));
    this.markupsBeforeRequestQuote = new WeakMap();
    this.optionsLoadedOnRequestQuote = new WeakSet();
    // Initialize form controls based on provided controls
    const formControls: Record<string, any> = {};

    const { controls: controlsList } = getControlsForGeneralCategory(
      data.category_id || this.initialSelectedCategory(),
      this.categories()
    );
    // Add all other controls first
    if (controlsList.includes(ProductControls.ProductName)) {
      formControls[ProductControls.ProductName] = new FormControl(
        data.product_name || "",
        Validators.required
      );
    }
    // Optional everyday name; blank by default. When set, storefront product
    // cards read "<common name> (<product name>)".
    if (controlsList.includes(ProductControls.CommonName)) {
      formControls[ProductControls.CommonName] = new FormControl(data.common_name || "", [
        Validators.maxLength(120),
      ]);
    }
    if (controlsList.includes(ProductControls.Qty)) {
      formControls[ProductControls.Qty] = new FormControl(data.qty || 0, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);
    }

    if (controlsList.includes(ProductControls.MinSize)) {
      formControls[ProductControls.MinSize] = new FormControl(data.min_size || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }

    if (controlsList.includes(ProductControls.MaxSize)) {
      formControls[ProductControls.MaxSize] = new FormControl(data.max_size || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }

    if (controlsList.includes(ProductControls.IsPromoted)) {
      formControls[ProductControls.IsPromoted] = new FormControl(data.is_promoted || false, [
        Validators.required,
      ]);
    }
    if (controlsList.includes(ProductControls.Tags)) {
      const trimmedTags = (data.tags || "").trim();
      formControls[ProductControls.Tags] = new FormControl(
        trimmedTags ? trimmedTags.split(",") : []
      );
    }
    if (controlsList.includes(ProductControls.AiTags)) {
      const rawAiTags = (data as any).ai_tags;
      formControls[ProductControls.AiTags] = new FormControl(
        Array.isArray(rawAiTags) ? rawAiTags : []
      );
    }
    // Store vendor_id to set after vendors are loaded — coerce to number to avoid type mismatch
    // when comparing with vendorOptions (users.id may come back as string from BIGINT column)
    const vendorIdToSet = data.vendor_id ? +data.vendor_id : 0;

    if (controlsList.includes(ProductControls.OriginCountryIso2)) {
      formControls[ProductControls.OriginCountryIso2] = new FormControl(data.origin_iso2 || "", [
        Validators.required,
        Validators.pattern(iso2Pattern),
      ]);
      // Every vendor is loaded once; Vendor Country filters them in the form. A
      // saved product opens on its vendor's own country (set when the vendor
      // is), not the product's stored origin, which can be out of date.
      // untracked: a new product's form is built inside the page's init effect,
      // where Angular 17 refuses signal writes.
      untracked(() => this.vendorCountry.set(vendorIdToSet ? "" : String(data.origin_iso2 ?? "")));
      this.getVendors("", vendorIdToSet);
    }

    if (controlsList.includes(ProductControls.VendorId)) {
      // Initialize with 0, will be set after vendors are loaded (if origin_iso2 exists)
      // Otherwise set it immediately if no origin_iso2 is needed
      // min(1): the placeholder 0 keeps Save off until the vendor is restored.
      formControls[ProductControls.VendorId] = new FormControl(
        data.origin_iso2 ? 0 : vendorIdToSet,
        [Validators.required, Validators.min(1)]
      );
      // If vendor_id exists but origin_iso2 doesn't exist in controls, pre-fetch defaults
      // (ready for when the user adds a new country; do NOT overwrite existing saved fees)
      if (vendorIdToSet && !data.origin_iso2) {
        this.fetchAndStoreVendorDefaults(vendorIdToSet).then(() =>
          this.refreshOptionMarkups("new")
        );
      }
    }
    if (controlsList.includes(ProductControls.CategoryId)) {
      formControls[ProductControls.CategoryId] = new FormControl(data.category_id || 1, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);

      if (data.category_id) {
        this.getSubCategories(data.category_id);
      }
    }

    if (controlsList.includes(ProductControls.SubcategoryId)) {
      formControls[ProductControls.SubcategoryId] = new FormControl(data.subcategory_id || 0, [
        Validators.required,
        Validators.pattern(phoneNumberPattern),
      ]);

      if (data.subcategory_id) {
        this.getSubSubCategories(data.subcategory_id);
      }
    }

    if (controlsList.includes(ProductControls.SubsubcategoryId)) {
      formControls[ProductControls.SubsubcategoryId] = new FormControl(
        data.subsubcategory_id || 0,
        [Validators.required, Validators.pattern(phoneNumberPattern)]
      );
    }

    if (controlsList.includes(ProductControls.Status)) {
      formControls[ProductControls.Status] = new FormControl(
        data.status || ProductListingStatus.Active
      );
    }
    if (controlsList.includes(ProductControls.MoreDescription)) {
      formControls[ProductControls.MoreDescription] = new FormControl(
        data.more_description || "",
        Validators.required
      );
    }

    if (controlsList.includes(ProductControls.IsFeatured)) {
      formControls[ProductControls.IsFeatured] = new FormControl(data.is_featured || false, [
        Validators.required,
      ]);
    }

    if (controlsList.includes(ProductControls.IsRareFish)) {
      formControls[ProductControls.IsRareFish] = new FormControl(data.is_exclusive || false, [
        Validators.required,
      ]);
    }

    if (controlsList.includes(ProductControls.AvailableInBuildAquarium)) {
      formControls[ProductControls.AvailableInBuildAquarium] = new FormControl(
        data.available_in_build_aquarium || false
      );
    }

    if (controlsList.includes(ProductControls.IsPreOrder)) {
      formControls[ProductControls.IsPreOrder] = new FormControl(data.is_seasonal || false);
    }

    if (controlsList.includes(ProductControls.IsRequestQuote)) {
      formControls[ProductControls.IsRequestQuote] = new FormControl(
        (data as any).is_request_quote || false
      );
    }

    if (controlsList.includes(ProductControls.Temperament)) {
      formControls[ProductControls.Temperament] = new FormControl(data.temperament || "");
    }

    if (controlsList.includes(ProductControls.WaterCondition)) {
      formControls[ProductControls.WaterCondition] = new FormControl(data.water_condition || "");
    }

    if (controlsList.includes(ProductControls.Wysiwyg)) {
      formControls[ProductControls.Wysiwyg] = new FormControl(data.wysiwyg || "representative");
    }

    if (controlsList.includes(ProductControls.ProductExpiry)) {
      formControls[ProductControls.ProductExpiry] = new FormControl(
        data.expires_at ? new Date(data.expires_at) : null
      );
    }

    if (controlsList.includes(ProductControls.TankBreadth)) {
      formControls[ProductControls.TankBreadth] = new FormControl(data.tank_breadth || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }

    if (controlsList.includes(ProductControls.TankHeight)) {
      formControls[ProductControls.TankHeight] = new FormControl(data.tank_height || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }

    if (controlsList.includes(ProductControls.TankLength)) {
      formControls[ProductControls.TankLength] = new FormControl(data.tank_length || 0, [
        Validators.required,
        Validators.pattern(decimalNumber),
      ]);
    }
    if (controlsList.includes(ProductControls.CapacityInGallons)) {
      formControls[ProductControls.CapacityInGallons] = new FormControl(
        data.capacity_in_gallons || 0,
        [Validators.required, Validators.pattern(decimalNumber)]
      );
    }

    const media: IMediaPicker = {
      images: data.images?.map((i) => ({ ...i, state: "original" })) || [],
      videos: data.videos?.map((i) => ({ ...i, state: "original" })) || [],
    };

    if (controlsList.includes(ProductControls.ProductMedia)) {
      formControls[ProductControls.ProductMedia] = new FormControl(media || {});
    }

    // Create the main form
    this.productForm = this.formBuilder.group(formControls);
    this.watchRestrictions();
    this.watchAllowedMarkets();

    // When isRequestQuote toggles, disable/enable country price fields
    if (controlsList.includes(ProductControls.IsRequestQuote)) {
      this.productForm
        .get(ProductControls.IsRequestQuote)
        ?.valueChanges.subscribe((isRQ: boolean) => {
          // On: remember each option's markup. Off: put those back; options saved
          // with markup 0 under Request Quote take their sizes' markups again.
          if (isRQ) this.snapshotOptionMarkups();
          this.applyRequestQuoteState(isRQ);
          if (!isRQ) this.refreshOptionMarkups("requestQuoteOff");
          const preOrderCtrl = this.productForm.get(ProductControls.IsPreOrder);
          if (isRQ) {
            preOrderCtrl?.setValue(false, { emitEvent: false });
            preOrderCtrl?.disable({ emitEvent: false });
          } else {
            preOrderCtrl?.enable({ emitEvent: false });
          }
        });
    }

    // Initialize empty country specific form for new products
    this.countrySpecificForm = new FormGroup({});

    // If we have existing country data, initialize it
    this.loadedMarkets.set(
      Object.keys(data.countrySpecificInfo ?? {}).map((iso2) => iso2.toUpperCase())
    );
    if (data.countrySpecificInfo) {
      const countryGroups: Record<string, FormGroup> = {};
      Object.entries(data.countrySpecificInfo!).forEach(([iso2, info]) => {
        this.selectedCountries.update((countries) => [...countries, iso2]);
        countryGroups[iso2] = this.createCountryFormGroup(iso2, info);
      });
      this.countrySpecificForm = new FormGroup(countryGroups);
      // collapsedCountries starts empty, so all loaded countries are expanded by default
    }

    // Apply Request Quote state on initial load (e.g. editing a product already set to
    // Request Quote) so display price & delivery charge are zeroed + disabled and the
    // form stays valid/saveable. Without this they load enabled with 0 and fail the
    // min(0.01)/required validators, blocking save. Also grey out Pre-Order, which is
    // not applicable when Request Quote is on.
    if (controlsList.includes(ProductControls.IsRequestQuote)) {
      const isRQ = this.isRequestQuoteControl?.value ?? false;
      if (isRQ) {
        for (const iso2 of Object.keys(this.countrySpecificForm.controls)) {
          for (const option of this.getProductOptionsArray(iso2).controls) {
            this.optionsLoadedOnRequestQuote.add(option);
          }
        }
      }
      this.applyRequestQuoteState(isRQ);
      const preOrderCtrl = this.productForm.get(ProductControls.IsPreOrder);
      if (isRQ) {
        preOrderCtrl?.setValue(false, { emitEvent: false });
        preOrderCtrl?.disable({ emitEvent: false });
      } else {
        preOrderCtrl?.enable({ emitEvent: false });
      }
    }

    // Setup product expiry if it exists
    if (controlsList.includes(ProductControls.ProductExpiry)) {
      this.setupProductExpiry({ currentExpiry: data.expires_at });
    }

    // Setup form subscriptions
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }

    this.categorySubscription?.unsubscribe();
    this.categorySubscription = this.initialSelectedCategory$.subscribe((categoryId) => {
      if (!this.productId()) {
        this.categoryIdControl?.setValue(categoryId);
        this.onCategorySelectionChange(categoryId);
      }
    });

    printFormValueAndValidity(this.productForm);
    printFormValueAndValidity(this.countrySpecificForm);

    this.formSubscription = combineLatest([
      this.productForm.valueChanges.pipe(startWith(this.productForm.value)),
      this.productForm.statusChanges.pipe(startWith(this.productForm.status)),
      this.countrySpecificForm.valueChanges.pipe(startWith(this.countrySpecificForm.value)),
      this.countrySpecificForm.statusChanges.pipe(startWith(this.countrySpecificForm.status)),
    ]).subscribe(([productFormValue, productFormStatus, countryFormValue, _countryFormStatus]) => {
      const { productMedia, tags, ...info } = productFormValue;

      // Process country form value to ensure product options are included.
      // Use getRawValue on country group so disabled fields (displayPrice,
      // deliveryCharge, etc. when Request Quote is on) are included with 0.
      const processedCountryFormValue = Object.keys(this.countrySpecificForm.controls).reduce<
        Record<string, any>
      >((acc, iso2) => {
        const group = this.countrySpecificForm.get(iso2) as any;
        const rawCountryData = group?.getRawValue
          ? group.getRawValue()
          : countryFormValue?.[iso2] || {};
        const optionsArray = this.getProductOptionsArray(iso2);
        acc[iso2] = {
          ...rawCountryData,
          productOptions: optionsArray.getRawValue
            ? optionsArray.getRawValue()
            : optionsArray.value,
          ...(deliveryZonesFor(iso2).length > 0 && group
            ? { deliveryZoneFees: this.zoneFeesOf(iso2, group) }
            : {}),
        };
        return acc;
      }, {});

      // Emit combined form value
      const formValue = {
        ...info,
        ...productMedia,
        tags: ((tags || []) as unknown as string[])?.join(","),
        countrySpecificInfo: processedCountryFormValue,
      };
      this.formChanged.emit(formValue);
      console.log(formValue);

      // Validate country specific requirements.
      //
      // This is computed explicitly from the raw current values rather than
      // trusting `countrySpecificForm.status` (the destructured, now-unused
      // `_countryFormStatus` above). Angular's aggregate status for this
      // particular subtree is unreliable here: duplicateNameValidator (see
      // createCountryFormGroup) calls `getProductOptionsArray(iso2)`, which
      // resolves via `this.countrySpecificForm.get(iso2)` — but at the exact
      // moment each option's FormControl is constructed (inside the .map()
      // that builds the FormArray), `this.countrySpecificForm` hasn't been
      // reassigned to include this country yet, so that lookup returns an
      // empty array and the validator's very first run never sees real
      // sibling data. Whether a genuine duplicate ever gets caught after
      // that then depends on some later, incidental revalidation — which is
      // exactly what made Save silently flip to disabled (or stay wrongly
      // enabled) depending on unrelated user actions elsewhere in the form.
      // Recomputing straight from `country.productOptions` sidesteps that
      // entirely and stays correct regardless of validator timing.
      const hasCountries = Object.keys(processedCountryFormValue || {}).length > 0;
      const isRQ = this.isRequestQuoteControl?.value ?? false;
      const allCountriesValid =
        hasCountries &&
        Object.values(processedCountryFormValue || {}).every((country: any) => {
          const activeOptions = (country.productOptions || []).filter(
            (opt: any) => opt?.state !== "delete"
          );
          // Request Quote zeroes and disables every price (see
          // applyRequestQuoteState), so demanding price > 0 here made Save
          // permanently unreachable for those products — and unfixable, since
          // the input is disabled. Only the option's name is required then.
          // displayPriceOk below already exempts isRQ the same way; the backend
          // substitutes placeholder prices for validation and forces 0 after.
          const hasValidOptions = activeOptions.some(
            (opt: any) => !!opt.addon_name && (isRQ || Number(opt.price) > 0)
          );
          const seenNames = new Set<string>();
          const hasDuplicateOptionNames = activeOptions.some((opt: any) => {
            const name = (opt.addon_name || "").trim();
            if (!name) return false;
            if (seenNames.has(name)) return true;
            seenNames.add(name);
            return false;
          });
          const displayPriceOk =
            isRQ ||
            (Number(country.displayPrice) > 0 && decimalNumber.test(String(country.displayPrice)));
          const deliveryChargeOk =
            country.deliveryCharge !== null &&
            country.deliveryCharge !== undefined &&
            Number(country.deliveryCharge) >= 0 &&
            decimalNumber.test(String(country.deliveryCharge));
          return hasValidOptions && !hasDuplicateOptionNames && displayPriceOk && deliveryChargeOk;
        });

      // Emit combined form validity
      const isValid =
        productFormStatus === "VALID" &&
        (this.mediaPickerControl?.value.images?.filter((img) => img.state !== "delete")?.length ||
          0) > 0 &&
        hasCountries &&
        allCountriesValid &&
        !Object.keys(processedCountryFormValue).some((iso2) => this.marketBlocked(iso2));

      this.formIsValid.emit(isValid);
    });
  }

  setupProductExpiry(data: { currentExpiry?: string }) {
    if (data.currentExpiry) {
      const { text } = this.productService.getExpiryDisplayText(data.currentExpiry);
      this.productExpiryHintText = text;
    }

    // Subscribe to value changes to update hint text
    this.productExpiryControl?.valueChanges.subscribe((value) => {
      if (value) {
        const { text } = this.productService.getExpiryDisplayText(value);
        this.productExpiryHintText = text;
      } else {
        this.productExpiryHintText = "";
      }
    });
  }

  // Called when the vendor dropdown selection changes
  /** Vendor Country shows the chosen vendor's own country. */
  private syncVendorCountry(vendorId: number | null) {
    const iso2 = this.vendors().find((v) => +v.id === +(vendorId ?? 0))?.iso2;
    if (!iso2) return;
    this.originCountryIso2Control?.setValue(iso2);
    this.vendorCountry.set(iso2);
  }

  /** A different Vendor Country: the vendor must be chosen again from its vendors. */
  onOriginCountryChange() {
    this.vendorCountry.set(this.originCountryIso2Control?.value ?? "");
    this.vendorIdControl?.reset();
    this.vendorDefaults = {};
  }

  async onVendorSelectionChange(vendorId: number | null) {
    this.syncVendorCountry(vendorId);
    if (!vendorId) {
      this.vendorDefaults = {};
      return;
    }
    // Fetch and store defaults when vendor changes
    await this.fetchAndStoreVendorDefaults(vendorId);
    // Apply to all currently selected countries
    this.applyVendorDefaults();
    // Transhipper markups are per market, not per vendor: a different vendor
    // changes none of them, so saved options keep theirs. Options not saved yet
    // (made before the markups arrived) still take the market's.
    this.refreshOptionMarkups("new");
  }

  /** Request Quote going on: each option's markup now, to restore when it goes off. */
  private snapshotOptionMarkups(): void {
    this.markupsBeforeRequestQuote = new WeakMap();
    for (const iso2 of Object.keys(this.countrySpecificForm?.controls ?? {})) {
      for (const option of this.getProductOptionsArray(iso2).controls) {
        const value = option.get("destination_transhipper_markup")?.value;
        if (value !== null && value !== undefined && value !== "") {
          this.markupsBeforeRequestQuote.set(option, Number(value));
        }
      }
    }
  }

  /**
   * Re-fills option markups from the market's markups for each option's packing
   * size (or "No Transhipment" when none is chosen), skipping any markup the
   * admin typed. An option made before the markups arrived - a
   * country ticked before the vendor was picked, say - would otherwise keep 0.
   * "new": options not saved yet, or whose size was just chosen.
   * "requestQuoteOff": a product coming off Request Quote - each option gets back
   * the markup it had when Request Quote went on; otherwise only one at 0, or
   * loaded on Request Quote (saved with 0), takes its size's markup. A custom
   * markup is never overwritten.
   */
  private refreshOptionMarkups(scope: "new" | "requestQuoteOff") {
    if (!this.countrySpecificForm) return;
    for (const iso2 of Object.keys(this.countrySpecificForm.controls)) {
      const hasDefaults = Boolean(this.vendorDefaults[iso2]);
      if (!hasDefaults && scope === "new") continue;
      const lookup = this.markupForSize(iso2);
      for (const option of this.getProductOptionsArray(iso2).controls) {
        const markupCtrl = option.get("destination_transhipper_markup");
        const stateCtrl = option.get("state");
        if (!markupCtrl || markupCtrl.dirty) continue;
        let markup: number | null;
        if (scope === "requestQuoteOff") {
          const before = this.markupsBeforeRequestQuote.get(option);
          const loadedOnRQ = this.optionsLoadedOnRequestQuote.has(option);
          if (before !== undefined) markup = before;
          else if (loadedOnRQ || Number(markupCtrl.value) === 0) {
            // Without the market's markups here this is null: the backend fills the
            // size's markup in on save, rather than the option keeping 0.
            markup = lookup(option.get("packing_size")?.value ?? NOT_APPLICABLE);
          } else continue;
          // Taken care of now (a markup is set below); a later off/on/off keeps what it has.
          this.optionsLoadedOnRequestQuote.delete(option);
        } else {
          // "new" also takes a saved option whose size was just changed.
          const sizeChanged = option.get("packing_size")?.dirty;
          if (stateCtrl?.value !== "add" && !sizeChanged) continue;
          markup = lookup(option.get("packing_size")?.value ?? NOT_APPLICABLE);
        }
        if (sameMarkup(markupCtrl.value, markup)) continue;
        markupCtrl.setValue(markup);
        // A saved option whose markup changed must be saved as an edit.
        if (stateCtrl?.value === "original" || stateCtrl?.value === "view") {
          option.markAsDirty();
          stateCtrl.setValue("edit");
        }
      }
    }
  }

  // Split the original function into two
  private async fetchAndStoreVendorDefaults(vendorId: number) {
    if (!vendorId) return;

    const res = await firstValueFrom(
      this.requestService.get<IApiResponse>(`/api/v2/vendor-delivery/${vendorId}`)
    );

    if (res.status && Array.isArray(res.data)) {
      // API returns an array of rows keyed by iso2; convert to a Record for fast lookup.
      // Backend aliases are camelCase (standardDeliveryFee / destinationTranshipperMarkup).
      this.vendorDefaults = {};
      for (const row of res.data) {
        this.vendorDefaults[row.iso2] = {
          standard_delivery_fee: row.standardDeliveryFee ?? 0,
          delivery_zone_fees: row.deliveryZoneFees ?? null,
          destination_transhipper_markup: row.destinationTranshipperMarkup ?? 0,
          size_markups: row.sizeMarkups ?? {},
        };
      }
    }
  }

  /**
   * The market's markup for a packing size (the Transhipment page) - what an
   * option's markup is filled in with when its size is chosen. "na" (No
   * Transhipment), or no size, takes the market's No Transhipment markup; a
   * size the market has not set is 0, as the backend reads it. Null while the
   * market's markups are not here (none for this vendor and market, or not
   * loaded yet): saved empty, the backend fills the size's markup in.
   */
  private readonly markupLookups: Record<string, (size: string) => number | null> = {};
  markupForSize(iso2: string): (size: string) => number | null {
    this.markupLookups[iso2] ??= (size: string) => {
      const d = this.vendorDefaults[iso2];
      if (!d) return null;
      if (!size || size === NOT_APPLICABLE) return d.destination_transhipper_markup ?? 0;
      return Number(d.size_markups?.[size]) || 0;
    };
    return this.markupLookups[iso2];
  }

  private applyVendorDefaults(iso2?: string) {
    // Only auto-populate on new products. For existing products the saved fees
    // are authoritative and must never be overwritten by vendor defaults.
    if (this.productId()) return;

    const applyForCountry = (countryIso2: string) => {
      const defaults = this.vendorDefaults[countryIso2];
      if (!defaults) return;

      const deliveryCtrl = this.getCountryControl(countryIso2, "deliveryCharge");
      const markupCtrl = this.getCountryControl(countryIso2, "destinationTranshipperMarkup");

      // Only fill fields the user has not yet edited (value is still the initial 0)
      if (!deliveryCtrl.value) {
        deliveryCtrl.patchValue(defaults.standard_delivery_fee ?? 0);
      }
      // Zones (US, MY) prefill from the vendor's zone defaults, or its single fee if
      // it has none yet — same "only if untouched" rule.
      for (const z of deliveryZonesFor(countryIso2)) {
        const ctrl = this.getCountryControl(countryIso2, zoneControlName(z.zone));
        if (ctrl && !ctrl.value) {
          ctrl.patchValue(
            defaults.delivery_zone_fees?.[String(z.zone) as keyof ZoneFees] ??
              defaults.standard_delivery_fee ??
              0
          );
        }
      }
      if (!markupCtrl.value) {
        markupCtrl.patchValue(defaults.destination_transhipper_markup ?? 0);
      }
    };

    if (iso2 && iso2 !== "All") {
      applyForCountry(iso2);
    } else {
      this.selectedCountries().forEach(applyForCountry);
    }
  }

  // Update onDestinationCountryChange to handle backend cleanup
  onDestinationCountryChange(event: MatCheckboxChange, iso2: string) {
    if (event.checked) {
      // Add country to form
      this.selectedCountries.update((countries) => [...countries, iso2]);
      // Ensure panel is not in collapsed set (so it opens by default)
      this.collapsedCountries.update((s) => {
        const next = new Set(s);
        next.delete(iso2);
        return next;
      });

      // Initialize countrySpecificForm if it doesn't exist
      if (!this.countrySpecificForm) {
        this.countrySpecificForm = new FormGroup({});
      }

      const newGroup = this.createCountryFormGroup(iso2);
      this.countrySpecificForm.addControl(iso2.toString(), newGroup);
      // If isRequestQuote is active, immediately disable price fields on the new group
      if (this.isRequestQuoteControl?.value) {
        this.applyRequestQuoteState(true);
      }
      this.applyVendorDefaults(iso2);

      // Add an initial empty option if there are no options
      const optionsArray = this.getProductOptionsArray(iso2);
      if (optionsArray.length === 0) {
        this.addProductOption(iso2);
      }
    } else {
      // Remove country from form
      this.selectedCountries.update((countries) => countries.filter((c) => c !== iso2));
      // Remove from collapsed set too (clean up)
      this.collapsedCountries.update((s) => {
        const next = new Set(s);
        next.delete(iso2);
        return next;
      });

      // Mark all product options for this country as deleted
      const optionsArray = this.getProductOptionsArray(iso2);
      optionsArray.controls.forEach((control) => {
        if (control.get("id")?.value) {
          control.patchValue({ state: "delete" });
        }
      });

      // Remove the form group but keep deleted options for backend processing
      const countryGroup = this.countrySpecificForm.get(iso2.toString());
      if (countryGroup) {
        this.countrySpecificForm.removeControl(iso2.toString());
      }

      // If no countries left, clean up the form
      if (this.selectedCountries().length === 0) {
        this.countrySpecificForm = new FormGroup({});
      }
    }
  }

  isCountryExpanded(iso2: string): boolean {
    return !this.collapsedCountries().has(iso2);
  }

  onPanelClosed(iso2: string) {
    this.collapsedCountries.update((s) => {
      const next = new Set(s);
      next.add(iso2);
      return next;
    });
  }

  onPanelOpened(iso2: string) {
    this.collapsedCountries.update((s) => {
      const next = new Set(s);
      next.delete(iso2);
      return next;
    });
  }

  onCategorySelectionChange(categoryId: number) {
    this.subcategoryIdControl?.patchValue(0);
    if (this.subsubcategoryIdControl) {
      this.subsubcategoryIdControl.patchValue(0);
    }
    this.getSubCategories(categoryId);
  }

  onSubcategorySelectionChange(subCategoryId: number) {
    if (this.subsubcategoryIdControl) {
      this.subsubcategoryIdControl.patchValue(0);
    }
    this.getSubSubCategories(subCategoryId);
  }

  getSubCategories(categoryId: number) {
    this.requestService
      .get(`/api/v2/categories?parent_id=${categoryId}&status=active`)
      .subscribe((res: any) => {
        if (res.status === true) {
          // Map to include 'name' field for compatibility with autocomplete
          const data = (res.data?.categories || []).map((cat: any) => ({
            ...cat,
            name: cat.category_name,
          }));
          this.subCategories.set(data);
        }
      });
  }

  getSubSubCategories(subCategoryId: number) {
    this.requestService
      .get(`/api/v2/categories?parent_id=${subCategoryId}&status=active`)
      .subscribe((res: any) => {
        if (res.status === true) {
          // Map to include 'name' field for compatibility with autocomplete
          const data = (res.data?.categories || []).map((cat: any) => ({
            ...cat,
            name: cat.category_name,
          }));
          this.subSubCategories.set(data);
        }
      });
  }

  onExpirySelectionChange(expiryValue: string) {
    if (expiryValue) {
      this.productExpiryHintText = this.getExpiryTextWithDate(expiryValue);
    } else {
      this.productExpiryHintText = "Hit save to remove expiry.";
    }
  }

  get productNameControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.ProductName) as FormControl<string>;
  }

  get commonNameControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.CommonName) as FormControl<string>;
  }

  get qtyControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.Qty) as FormControl<number>;
  }

  get minSizeControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.MinSize) as FormControl<number>;
  }

  get maxSizeControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.MaxSize) as FormControl<number>;
  }

  get isPromotedControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.IsPromoted) as FormControl<boolean>;
  }

  get tagsDataControl(): FormControl<string[]> | undefined {
    return this.productForm.get(ProductControls.Tags) as FormControl<string[]>;
  }

  get aiTagsControl(): FormControl<string[]> | undefined {
    return this.productForm.get(ProductControls.AiTags) as FormControl<string[]>;
  }

  get originCountryIso2Control(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.OriginCountryIso2) as FormControl<string>;
  }

  get vendorIdControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.VendorId) as FormControl<number>;
  }

  get categoryIdControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.CategoryId) as FormControl<number>;
  }

  get statusControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.Status) as FormControl<string>;
  }

  get moreDescriptionControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.MoreDescription) as FormControl<string>;
  }

  get isFeaturedControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.IsFeatured) as FormControl<boolean>;
  }

  get isRareFishControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.IsRareFish) as FormControl<boolean>;
  }

  get availableInBuildAquariumControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.AvailableInBuildAquarium) as FormControl<boolean>;
  }

  get subcategoryIdControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.SubcategoryId) as FormControl<number>;
  }

  get subsubcategoryIdControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.SubsubcategoryId) as FormControl<number>;
  }

  get isPreOrderControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.IsPreOrder) as FormControl<boolean>;
  }

  get isRequestQuoteControl(): FormControl<boolean> | undefined {
    return this.productForm.get(ProductControls.IsRequestQuote) as FormControl<boolean>;
  }

  /** Set price fields to 0 and disable/enable them based on isRequestQuote toggle */
  applyRequestQuoteState(isRQ: boolean): void {
    const applyToControl = (ctrl: AbstractControl | null) => {
      if (!ctrl) return;
      if (isRQ) {
        ctrl.setValue(0, { emitEvent: false });
        ctrl.disable({ emitEvent: false });
      } else {
        ctrl.enable({ emitEvent: false });
      }
    };

    Object.keys(this.countrySpecificForm.controls).forEach((iso2) => {
      const group = this.countrySpecificForm.get(iso2);
      if (!group) return;
      const priceFields = [
        "displayPrice",
        "deliveryCharge",
        "destinationTranshipperMarkup",
        ...ALL_ZONE_CONTROL_NAMES,
      ];
      priceFields.forEach((field) => applyToControl(group.get(field)));

      // Also zero/disable each product option's price so the form stays valid for
      // Request Quote on initial load (the child component only does this during change
      // detection, which is too late for the initial validity computation).
      const options = group.get("productOptions") as FormArray;
      options?.controls.forEach((opt) => applyToControl(opt.get("price")));

      // Turning Request Quote off re-enabled the charges: a walk-in-only market keeps them off.
      if (group.get("walkInOnly")?.value) this.applyWalkInOnly(group as FormGroup, true);
    });
  }

  get temperamentControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.Temperament) as FormControl<string>;
  }

  get waterConditionControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.WaterCondition) as FormControl<string>;
  }

  get wysiwygControl(): FormControl<string> | undefined {
    return this.productForm.get(ProductControls.Wysiwyg) as FormControl<string>;
  }

  get productExpiryControl(): FormControl<Date> | undefined {
    return this.productForm.get(ProductControls.ProductExpiry) as FormControl<Date>;
  }

  get tankBreadthControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.TankBreadth) as FormControl<number>;
  }

  get tankLengthControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.TankLength) as FormControl<number>;
  }

  get tankHeightControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.TankHeight) as FormControl<number>;
  }

  get capacityControl(): FormControl<number> | undefined {
    return this.productForm.get(ProductControls.CapacityInGallons) as FormControl<number>;
  }

  get mediaPickerControl(): FormControl<IMediaPicker> | undefined {
    return this.productForm.get(ProductControls.ProductMedia) as FormControl<IMediaPicker>;
  }

  isCountrySelected(iso2: string): boolean {
    return this.selectedCountries().some((c) => c === iso2);
  }

  getCountryControl(iso2: string, controlName: string): FormControl | FormArray {
    const countryGroup = this.countrySpecificForm.get(`${iso2}`);
    if (!countryGroup) return new FormControl(); // Return dummy control if group doesn't exist

    const control = countryGroup.get(controlName);
    if (!control) return new FormControl(); // Return dummy control if control doesn't exist

    return control as FormControl | FormArray;
  }

  getProductOptionsArray(iso2: string): FormArray<FormGroup> {
    const countryGroup = this.countrySpecificForm.get(`${iso2}`) as FormGroup;
    if (!countryGroup) return new FormArray<FormGroup>([]); // Return empty array if group doesn't exist

    const optionsArray = countryGroup.get("productOptions") as FormArray<FormGroup>;
    if (!optionsArray) {
      // If productOptions array doesn't exist, create it
      const newArray = new FormArray<FormGroup>([]);
      countryGroup.addControl("productOptions", newArray);
      return newArray;
    }
    return optionsArray;
  }

  addProductOption(iso2: string) {
    const optionsArray = this.getProductOptionsArray(iso2);
    const newOption = new FormGroup({
      addon_name: new FormControl("", [Validators.required, this.duplicateNameValidator(iso2)]),
      price: new FormControl(0, [
        Validators.required,
        Validators.pattern(decimalNumber),
        Validators.min(0.01),
      ]),
      status: new FormControl("active"),
      state: new FormControl("add"),
      // "No Transhipment" until a size is chosen, with the market's markup for it
      // in this country - saved as such, not as an unset (legacy) size.
      packing_size: new FormControl<string | null>(NOT_APPLICABLE),
      destination_transhipper_markup: new FormControl(this.markupForSize(iso2)(NOT_APPLICABLE), [
        Validators.min(0),
        Validators.pattern(decimalNumber),
      ]),
    });
    optionsArray.push(newOption);
    // Trigger form value changes
    this.countrySpecificForm.updateValueAndValidity();
  }

  private duplicateNameValidator(iso2: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) return null;

      const optionsArray = this.getProductOptionsArray(iso2);
      const currentFormGroup = control.parent;

      const hasDuplicate = optionsArray.controls.some(
        (group) =>
          group !== currentFormGroup &&
          group.get("addon_name")?.value === control.value &&
          group.get("state")?.value !== "delete"
      );

      return hasDuplicate ? { duplicateName: true } : null;
    };
  }

  removeProductOption(iso2: string, index: number) {
    const optionsArray = this.getProductOptionsArray(iso2);
    const option = optionsArray.at(index);
    if (option.get("id")?.value) {
      // If the option has an ID, mark it for deletion instead of removing.
      // The row is hidden from the template once state is "delete" (see
      // product-options.component.html), but the FormGroup stays in the
      // FormArray so getRawValue() still submits it to the backend. If we
      // leave it enabled, whatever validity it had at the moment of deletion
      // (e.g. a duplicateName error from a sibling option that shared its
      // name) is frozen forever — nothing re-runs its validators once it's
      // hidden, so it can permanently invalidate the whole form with no
      // visible error anywhere. Disabling it excludes it from the parent
      // FormGroup/FormArray's validity rollup while getRawValue() still
      // includes disabled controls' values.
      option.patchValue({ state: "delete" });
      option.disable({ emitEvent: false });

      // Disabling only stops THIS option's own (possibly stale) errors from
      // counting toward the parent's validity — it does nothing for a
      // surviving sibling that was previously flagged as a duplicate of the
      // option we just deleted. That sibling's addon_name control cached
      // `duplicateName: true` the last time it ran and nothing re-runs it
      // just because its duplicate disappeared, so it stays invalid forever
      // with no visible error (the mat-error only shows once touched).
      // Re-running each remaining option's validators clears that.
      optionsArray.controls.forEach((sibling) => {
        if (sibling !== option && sibling.get("state")?.value !== "delete") {
          sibling.get("addon_name")?.updateValueAndValidity();
        }
      });
    } else {
      // If it's a new option, remove it completely
      optionsArray.removeAt(index);
    }
    // Trigger form value changes
    this.countrySpecificForm.updateValueAndValidity();
  }

  private getExpiryTextWithDate(date: string) {
    return `Expires on ${new Date(date).toLocaleDateString()} at 11:59PM`;
  }

  getVendors(iso2: string, vendorIdToSet?: number) {
    const url = iso2
      ? `/api/v2/active-vendor?iso2=${encodeURIComponent(iso2)}`
      : `/api/v2/active-vendor`;
    this.requestService.get<IApiResponse>(url).subscribe((res) => {
      if (res.status) {
        this.vendors.set(res.data);
        // Defer setValue to the next tick so Angular change detection can propagate
        // the vendors signal to the child's options() input before writeValue runs
        if (vendorIdToSet && this.vendorIdControl) {
          setTimeout(async () => {
            if (this.vendorIdControl) {
              this.vendorIdControl.setValue(vendorIdToSet);
              this.syncVendorCountry(vendorIdToSet);
              // Pre-fetch vendor defaults so they are ready when the user adds a new country.
              // Do NOT apply them here — existing saved delivery fees must not be overwritten.
              await this.fetchAndStoreVendorDefaults(vendorIdToSet);
              // Options added before the markups arrived take them now.
              this.refreshOptionMarkups("new");
            }
          }, 0);
        }
      }
    });
  }

  private createCountryFormGroup(iso2: string, existingData?: any): FormGroup {
    const group = this.buildCountryFormGroup(iso2, existingData);
    if (deliveryZonesFor(iso2).length > 0) {
      this.addZoneControls(iso2, group, existingData);
      // Walk-in was applied before the zones existed (and syncing them refilled
      // the single charge), so apply it again now they do.
      this.applyWalkInOnly(group, Boolean(group.get("walkInOnly")?.value));
    }
    return group;
  }

  /**
   * A zoned market (US: five zones, MY: two) replaces the single delivery charge
   * with zone fees, charged at checkout by the customer's shipping-address state. deliveryCharge stays in the form,
   * hidden, held at the highest zone: the existing validity checks and save path
   * read it, and it is the fallback checkout uses for a state it cannot match.
   */
  private addZoneControls(iso2: string, group: FormGroup, existingData?: any) {
    const saved = (existingData?.deliveryZoneFees ?? null) as ZoneFees | null;
    for (const z of deliveryZonesFor(iso2)) {
      // A product that predates zones starts every zone at its existing single fee,
      // so saving without editing leaves what customers pay unchanged.
      const initial =
        saved?.[String(z.zone) as keyof ZoneFees] ?? existingData?.deliveryCharge ?? 0;
      group.addControl(
        zoneControlName(z.zone),
        new FormControl(initial, [
          Validators.required,
          Validators.min(0),
          Validators.pattern(decimalNumber),
        ])
      );
    }
    const syncSingleFee = () =>
      group
        .get("deliveryCharge")
        ?.setValue(highestZoneFee(this.zoneFeesOf(iso2, group)), { emitEvent: false });
    for (const z of deliveryZonesFor(iso2)) {
      group.get(zoneControlName(z.zone))?.valueChanges.subscribe(syncSingleFee);
    }
    syncSingleFee();
  }

  /** A zoned country group's fees as {"1": n, ..., "n": n}. */
  private zoneFeesOf(iso2: string, group: AbstractControl): ZoneFees {
    const fees: ZoneFees = {};
    for (const z of deliveryZonesFor(iso2)) {
      fees[String(z.zone) as keyof ZoneFees] =
        Number(group.get(zoneControlName(z.zone))?.value) || 0;
    }
    return fees;
  }

  /**
   * Collected in person, so there is nothing to deliver and nothing to charge
   * for it. The charge is zeroed and disabled rather than merely ignored: a
   * number left visible next to a ticked box reads as one that will still be
   * billed. The server zeroes it again on save, because a disabled control
   * still submits whatever value it holds.
   */
  private applyWalkInOnly(group: FormGroup, walkInOnly: boolean): void {
    const chargeControls = ["deliveryCharge", ...ALL_ZONE_CONTROL_NAMES];
    for (const name of chargeControls) {
      const control = group.get(name);
      if (!control) continue;
      if (walkInOnly) {
        control.setValue(0, { emitEvent: false });
        control.disable({ emitEvent: false });
      } else if (!this.isRequestQuoteControl?.value) {
        // Unticking walk-in must not undo Request Quote, which keeps the charges zeroed.
        control.enable({ emitEvent: false });
      }
    }
  }

  private buildCountryFormGroup(iso2: string, existingData?: any): FormGroup {
    const group = new FormGroup({
      iso2: new FormControl(iso2),
      displayPrice: new FormControl(existingData?.displayPrice ?? 0, [
        Validators.required,
        Validators.min(0.01),
        Validators.pattern(decimalNumber),
      ]),
      deliveryCharge: new FormControl(existingData?.deliveryCharge ?? 0, [
        Validators.required,
        Validators.min(0),
        Validators.pattern(decimalNumber),
      ]),
      destinationTranshipperMarkup: new FormControl(
        existingData?.destinationTranshipperMarkup ?? 0,
        [Validators.pattern(decimalNumber), Validators.min(0)]
      ),
      walkInOnly: new FormControl(existingData?.walkInOnly ?? false),
      productOptions: new FormArray(
        (existingData?.productOptions || []).map(
          (option: any) =>
            new FormGroup({
              id: new FormControl(option.id),
              addon_name: new FormControl(option.addon_name, [
                Validators.required,
                this.duplicateNameValidator(iso2),
              ]),
              price: new FormControl(option.price, [
                Validators.required,
                Validators.pattern(decimalNumber),
                Validators.min(0.01),
              ]),
              status: new FormControl(option.status || "active"),
              state: new FormControl("original"),
              packing_size: new FormControl<string | null>(option.packing_size ?? null),
              destination_transhipper_markup: new FormControl(
                option.destination_transhipper_markup ?? 0,
                [Validators.min(0), Validators.pattern(decimalNumber)]
              ),
            })
        )
      ),
    });

    this.applyWalkInOnly(group, Boolean(existingData?.walkInOnly));
    group
      .get("walkInOnly")
      ?.valueChanges.subscribe((checked) => this.applyWalkInOnly(group, Boolean(checked)));

    return group;
  }

  isCountryFormInvalid(iso2: string): boolean {
    const countryGroup = this.countrySpecificForm.get(`${iso2}`) as FormGroup;
    if (!countryGroup) return true;

    // Check required fields correctly (allow 0 values)
    const displayPriceControl = countryGroup.get("displayPrice");
    const deliveryChargeControl = countryGroup.get("deliveryCharge");

    const hasEmptyRequiredFields =
      !displayPriceControl ||
      displayPriceControl.value === null ||
      displayPriceControl.value === undefined ||
      displayPriceControl.hasError?.("required") ||
      !deliveryChargeControl ||
      deliveryChargeControl.value === null ||
      deliveryChargeControl.value === undefined ||
      deliveryChargeControl.hasError?.("required");

    // Check if there are any valid product options
    const optionsArray = this.getProductOptionsArray(iso2);
    const activeOptions = optionsArray.controls.filter(
      (control) => control.get("state")?.value !== "delete"
    );
    // Same Request Quote exemption as the Save gate above, or the panel would
    // show "Needs info" forever on a product that is in fact complete.
    const isRQ = this.isRequestQuoteControl?.value ?? false;
    const hasValidOptions = activeOptions.some(
      (control) =>
        !!control.get("addon_name")?.value && (isRQ || Number(control.get("price")?.value) > 0)
    );

    // Kept in sync with the explicit duplicate check in the Save-gating
    // logic (see the combineLatest subscription above) rather than relying
    // on duplicateNameValidator's own cached error state, which can miss a
    // genuine duplicate at construction time or hold a stale error after a
    // duplicate sibling is deleted — see the comment there for why.
    const seenNames = new Set<string>();
    const hasDuplicateOptionNames = activeOptions.some((control) => {
      const name = (control.get("addon_name")?.value || "").trim();
      if (!name) return false;
      if (seenNames.has(name)) return true;
      seenNames.add(name);
      return false;
    });

    return hasEmptyRequiredFields || !hasValidOptions || hasDuplicateOptionNames;
  }

  generateDescription() {
    const productName = this.productNameControl?.value?.trim();
    if (!productName) {
      alert("Please enter a product name before generating a description.");
      return;
    }

    this.isGeneratingDescription.set(true);

    const body: Record<string, any> = { product_name: productName };
    if (this.temperamentControl?.value) body.temperament = this.temperamentControl.value;
    if (this.waterConditionControl?.value) body.water_condition = this.waterConditionControl.value;
    if (this.minSizeControl?.value != null) body.min_size = this.minSizeControl.value;
    if (this.maxSizeControl?.value != null) body.max_size = this.maxSizeControl.value;

    this.requestService
      .post<{ status: boolean; data: { description: string }; message: string }>(
        "/api/v2/generate-description",
        body
      )
      .subscribe({
        next: (res) => {
          if (res.status && res.data?.description) {
            this.moreDescriptionControl?.setValue(res.data.description);
            this.moreDescriptionControl?.markAsDirty();
          } else {
            alert(res.message || "Failed to generate description.");
          }
          this.isGeneratingDescription.set(false);
        },
        error: () => {
          alert("An error occurred while generating the description.");
          this.isGeneratingDescription.set(false);
        },
      });
  }

  ngOnDestroy() {
    this.restrictionSubscription?.unsubscribe();
    this.allowedMarketsSubscription?.unsubscribe();
    this.categorySubscription?.unsubscribe();
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
  }

  // Add method to handle form submission response
  handleFormSubmissionResponse(response: any) {
    if (!response.status && response.data) {
      // Set Zod validation errors
      this.zodErrors.set(response.data);

      // Map Zod errors to form controls
      response.data.forEach((error: ZodValidationError) => {
        const pathParts = error.path.split(".");
        if (pathParts.length === 1) {
          // Root level form control
          const control = this.productForm.get(pathParts[0]);
          if (control) {
            control.setErrors({ zodError: error.message });
            control.markAsTouched();
          }
        } else if (pathParts[0] === "countrySpecificInfo") {
          // Country specific form controls
          const iso2 = pathParts[1];
          const countryGroup = this.countrySpecificForm.get(iso2);
          if (countryGroup) {
            if (pathParts[2] === "productOptions") {
              // Product options errors
              const optionsArray = this.getProductOptionsArray(iso2);
              const optionIndex = parseInt(pathParts[3], 10);
              const optionField = pathParts[4];
              const optionControl = optionsArray.at(optionIndex)?.get(optionField);
              if (optionControl) {
                optionControl.setErrors({ zodError: error.message });
                optionControl.markAsTouched();
              }
            } else {
              // Other country specific fields
              const control = countryGroup.get(pathParts[2]);
              if (control) {
                control.setErrors({ zodError: error.message });
                control.markAsTouched();
              }
            }
          }
        }
      });
    } else {
      // Clear Zod errors if submission was successful
      this.zodErrors.set([]);
    }
  }

  // Method to get error message for a control
  getErrorMessage(control: AbstractControl | null): string {
    if (!control) return "";

    if (control.hasError("required")) {
      return "This field is required";
    }
    if (control.hasError("min")) {
      return `Value must be at least ${control.getError("min").min}`;
    }
    if (control.hasError("pattern")) {
      return "Invalid format";
    }
    if (control.hasError("zodError")) {
      return control.getError("zodError");
    }
    return "";
  }

  // Method to check if control has errors and should show them
  shouldShowError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.dirty || control.touched);
  }

  getSelectedCountries() {
    return this.destinationCountries()
      .filter((country) => this.isCountrySelected(country.iso2))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  getUnselectedCountries() {
    return this.destinationCountries()
      .filter((country) => !this.isCountrySelected(country.iso2))
      .sort((a, b) => a.name.localeCompare(b.name));
  }
}
